import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowRight, Star } from "lucide-react";
import { useQuery } from "convex/react";
import { SiteLayout } from "@/components/site/layout";
import { HeroImage } from "@/components/site/hero-image";
import { Reveal } from "@/components/site/reveal";
import { ReviewsCarousel } from "@/components/site/reviews";
import { BeforeAfter } from "@/components/site/before-after";
import { LazyVideo } from "@/components/site/lazy-video";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  SERVICES,
  SERVICE_PAGES,
  SITE,
  AEO_FAQ_ITEMS,
  priceRangeLabel,
  mergeLiveServices,
  allServicePages,
  servicePagePath,
  type LiveServiceRow,
  type ServicePageDef,
} from "@/lib/site";
import { api } from "@/lib/convex";
import { useInnhold } from "@/lib/innhold";
import { localBusinessJsonLd, faqJsonLd } from "@/components/site/jsonld";
import servicesImg from "@/assets/services-porsche.webp";
import beforePorsche from "@/assets/before-porsche.webp";
import afterPorsche from "@/assets/after-porsche.webp";
import beforePorscheSm from "@/assets/before-porsche-sm.webp";
import afterPorscheSm from "@/assets/after-porsche-sm.webp";
import aboutVideoPoster from "@/assets/about-poster.webp";
import aboutVideo from "@/assets/porsche-drammen-576.mp4";

const TITLE = "Drammen Car Wash | Bilvask, Dekkskift & Oljeskift i Drammen";
const DESC = "Premium bilvask fra 250 kr, bilpleie og polering, dekkskift fra 350 kr og oljeskift i Drammen. Nedre Eikervei 47. Book time på under 2 minutter.";

type Anmeldelsestall = { rating: number | null; antall: number | null };

/**
 * Google-tallene fra Ascendly (synket fra Google hver morgen). Hentes paa
 * serveren, saa tallet staar riktig i HTML-en og i JSON-LD for Google og
 * AI-assistentene, ikke bare etter at siden har lastet. Foer stod «32» fast i
 * koden mens Google viste 42 (Rafael 2.10.2026).
 */
async function hentAnmeldelsestall(): Promise<Anmeldelsestall | null> {
  try {
    const res = await fetch("https://bold-pony-700.eu-west-1.convex.cloud/api/query", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: "reviewTracking:publicReviewSummary", args: { businessId: BUSINESS_ID }, format: "json" }),
    });
    const j = (await res.json()) as { value?: { rating: number | null; count: number | null } | null };
    return j.value ? { rating: j.value.rating, antall: j.value.count } : null;
  } catch {
    return null;
  }
}

const BUSINESS_ID = "jh77w8p1ewdwak4qqg7k8fjben8b8zn5";

export const Route = createFileRoute("/")({
  loader: () => hentAnmeldelsestall(),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd(undefined, loaderData)) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(AEO_FAQ_ITEMS)) },
    ],
  }),
  component: Index,
});

const FEATURED_PRICING_IDS = ["utvendig", "komplett", "premium"];
function Index() {
  // Tekstene eieren kan redigere fra Ascendly-appen. Se src/lib/innhold.ts.
  const t = useInnhold();
  // Serverens tall foerst, saa live fra Convex naar Google-synken har oppdatert dem.
  const fraServer = Route.useLoaderData();
  const live = useQuery(api.reviewTracking.publicReviewSummary, { businessId: BUSINESS_ID }) as
    | { rating: number | null; count: number | null }
    | null
    | undefined;
  const snitt = live?.rating ?? fraServer?.rating ?? SITE.rating;
  const antall = live?.count ?? fraServer?.antall ?? SITE.reviewCount;
  // Live Convex service data (price, description) matched onto the local SERVICES list by
  // name. Falls back to the local static values while this query is loading.
  const liveServicesRaw = useQuery(api.bookings.listServicesForBusiness, {
    businessId: BUSINESS_ID,
  }) as LiveServiceRow[] | undefined;
  const services = useMemo(() => mergeLiveServices(SERVICES, liveServicesRaw), [liveServicesRaw]);
  // Prislisten paa forsiden viser tre kort. Hvilke tre er kuratert, men huker eieren av "mest
  // populaer" paa en tjeneste i appen, maa den tjenesten faktisk komme til syne. Derfor: de
  // avhukede foerst, deretter de kuraterte til vi har tre.
  const featuredServices = useMemo(() => {
    const valgt = services.filter((s) => s.featured);
    const resten = FEATURED_PRICING_IDS.map((id) => services.find((s) => s.id === id)).filter(
      (s): s is (typeof services)[number] => Boolean(s),
    );
    const ut: typeof services = [];
    for (const s of [...valgt, ...resten]) {
      if (ut.length >= FEATURED_PRICING_IDS.length) break;
      if (!ut.some((x) => x.id === s.id)) ut.push(s);
    }
    // Rekkefoelgen paa kortene er fast: utvendig til venstre, komplett i midten, premium til
    // hoeyre. En avhuket tjeneste utenfor lista havner ytterst til hoeyre.
    const plass = (id: string) => {
      const i = FEATURED_PRICING_IDS.indexOf(id);
      return i === -1 ? FEATURED_PRICING_IDS.length : i;
    };
    return ut.sort((a, b) => plass(a.id) - plass(b.id));
  }, [services]);

  /**
   * Tjenestene som ikke fikk et av de tre kortene.
   *
   * Seksjonen heter "Prisliste" og lover "alle vaare tjenester", men viste tre
   * kuraterte kort. La eieren til en ny tjeneste i Ascendly, kom den aldri til
   * syne her, og for ham var den da ikke paa nettsiden. Den laa paa /priser,
   * men det stedet maa man vite om.
   *
   * Lista kommer fra det samme sammenslaatte datagrunnlaget som kortene, saa
   * en ny tjeneste dukker opp av seg selv uten at noen roerer koden.
   */
  /**
   * Tjenestesiden som hoerer til en tjeneste, naar den finnes.
   *
   * Forsiden lenket til /book fra hvert eneste priskort og hver linje i
   * lista. Tjenestesidene fikk dermed ingen lenker fra den sterkeste siden
   * paa nettstedet, og Google rangerte forsiden i stedet for dem: «bilpleie
   * drammen» laa paa plass 54 med forsiden, mens /bilpleie-drammen ikke var
   * med i det hele tatt. Maalt 4. september 2026.
   *
   * Naa peker navnet paa tjenesten til siden om den, med tjenestens eget navn
   * som lenketekst. Bookingknappen staar urort ved siden av, saa den som vil
   * bestille med én gang fortsatt kan.
   */
  const sidePerTjeneste = useMemo(() => {
    const kart = new Map<string, ServicePageDef>();
    for (const side of allServicePages(liveServicesRaw)) {
      if (side.serviceId) kart.set(side.serviceId, side);
      if (side.dynamic) kart.set(side.preselect, side);
    }
    return kart;
  }, [liveServicesRaw]);

  const restenAvTjenestene = useMemo(
    () => services.filter((s) => !featuredServices.some((f) => f.id === s.id)),
    [services, featuredServices],
  );

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-start overflow-hidden">
        <HeroImage />
        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-10 pt-28 sm:pt-32 md:pt-36">
          <div className="max-w-2xl">
            <Reveal delay={140}>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-white text-balance [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]">
                {t("forside.hero.tittel1")}
                <br />
                {t("forside.hero.tittel2")}{" "}
                <span className="text-primary">{t("forside.hero.uthevet")}</span>
              </h1>
            </Reveal>
            <Reveal delay={420}>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link
                  to="/book"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
                >
                  {t("felles.bookknapp")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRICING */}
      {t.vis("forside.priser.vis") && (
      <section data-ascendly-seksjon="forside.priser.vis" id="tjenester" className="py-20 sm:py-28 md:py-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <div className="eyebrow">{t("forside.priser.stikktittel")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-balance">{t("forside.priser.tittel")}</h2>
            <p className="mt-4 text-muted-foreground">{t("forside.priser.tekst")}</p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featuredServices.map((tj, i) => (
              <Reveal key={tj.id} delay={i * 120}>
                <div
                  className={`h-full rounded-2xl border p-8 flex flex-col bg-card lift ${
                    tj.featured ? "border-primary/60 glow" : "border-border shadow-soft"
                  }`}
                >
                  {tj.featured && (
                    <div className="self-start rounded-full bg-primary/15 text-primary text-xs px-3 py-1 mb-4 tracking-wide">
                      {t("felles.mestpopulaer")}
                    </div>
                  )}
                  <h3 className="font-serif text-2xl">
                    {sidePerTjeneste.has(tj.id) ? (
                      <Link
                        to={servicePagePath(sidePerTjeneste.get(tj.id)!) as any}
                        className="transition-colors hover:text-primary"
                      >
                        {tj.name}
                      </Link>
                    ) : (
                      tj.name
                    )}
                  </h3>
                  <div className="mt-2 text-primary text-lg">{priceRangeLabel(tj)}</div>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{tj.desc}</p>
                  <div className="mt-auto pt-8">
                    <Link
                      to="/book"
                      search={{ service: tj.id } as never}
                      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                        tj.featured
                          ? "bg-primary-strong text-primary-foreground"
                          : "border border-border text-foreground hover:border-primary hover:text-primary"
                      }`}
                    >
                      {t("felles.bookknapp")} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {restenAvTjenestene.length > 0 && (
            <Reveal delay={160} className="mt-12">
              <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
                <div className="eyebrow">{t("forside.priser.ogsa")}</div>
                <ul className="mt-5 grid gap-x-10 gap-y-1 sm:grid-cols-2">
                  {restenAvTjenestene.map((tj) => {
                    const side = sidePerTjeneste.get(tj.id);
                    const innhold = (
                      <>
                        <span className="text-foreground">{tj.name}</span>
                        <span className="shrink-0 text-muted-foreground">{priceRangeLabel(tj)}</span>
                      </>
                    );
                    const klasse =
                      "flex items-baseline justify-between gap-4 border-b border-border/60 py-3 text-sm transition-colors hover:text-primary";
                    return (
                      <li key={tj.id}>
                        {side ? (
                          <Link to={servicePagePath(side) as any} className={klasse}>
                            {innhold}
                          </Link>
                        ) : (
                          <Link to="/book" search={{ service: tj.id } as never} className={klasse}>
                            {innhold}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Reveal>
          )}

          <div className="mt-10 flex justify-center">
            <Link
              to="/priser"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
            >
              {t("forside.priser.lenke")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* BEFORE / AFTER */}
      {t.vis("forside.resultater.vis") && (
      <section data-ascendly-seksjon="forside.resultater.vis" className="surface-light py-20 sm:py-28 md:py-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <div className="eyebrow" style={{ color: "oklch(0.68 0.02 255)" }}>{t("forside.resultater.stikktittel")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-balance" style={{ color: "var(--surface-light-foreground)" }}>
              {t("forside.resultater.tittel")}
            </h2>
            <p className="mt-4" style={{ color: "oklch(0.68 0.02 255)" }}>{t("forside.resultater.tekst")}</p>
          </Reveal>
          <Reveal delay={100} className="mt-14 max-w-4xl mx-auto">
            <BeforeAfter
              before={beforePorsche}
              after={afterPorsche}
              beforeSmall={beforePorscheSm}
              afterSmall={afterPorscheSm}
              alt="Porsche 911 vasket hos Drammen Car Wash"
            />
          </Reveal>
        </div>
      </section>
      )}

      {/* SERVICES DETAIL */}
      {t.vis("forside.bildebanner.vis") && (
      <section data-ascendly-seksjon="forside.bildebanner.vis" className="relative">
        <div className="relative min-h-[60vh] sm:min-h-[65vh] flex items-end overflow-hidden">
          <img
            src={servicesImg}
            alt="Svart Porsche i bevegelse, bilvask hos Drammen Car Wash i Drammen"
            loading="lazy"
            decoding="async"
            width={1537}
            height={1023}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 gradient-hero" />
          <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-10 pt-32 pb-24 sm:pb-28">
            <Reveal>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-white text-balance max-w-2xl">
                {t("forside.bildebanner.tittel")}
              </h2>
            </Reveal>
          </div>
        </div>
      </section>
      )}

      {/* REVIEWS */}
      {t.vis("forside.anmeldelser.vis") && (
      <section data-ascendly-seksjon="forside.anmeldelser.vis" id="anmeldelser"className="py-20 sm:py-28 md:py-40 border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <div className="eyebrow">{t("forside.anmeldelser.stikktittel")}</div>
            <div className="mt-4 flex items-center justify-center gap-1 text-primary">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-current" />
              ))}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-4 text-balance">
              {snitt.toFixed(1)} stjerner fra {antall} fornøyde kunder i Drammen
            </h2>
          </Reveal>
          <div className="mt-14">
            <ReviewsCarousel />
          </div>
          <div className="mt-12 flex justify-center">
            <a
              href={SITE.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm text-foreground/85 hover:border-primary hover:text-primary transition-colors"
            >
              {t("forside.anmeldelser.lenke")} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
      )}

      {/* ABOUT */}
      {t.vis("forside.om.vis") && (
      <section data-ascendly-seksjon="forside.om.vis" className="py-20 sm:py-28 md:py-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid gap-14 md:grid-cols-2 items-center">
          <Reveal>
            <div className="eyebrow">{t("forside.om.stikktittel")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-balance">{t("forside.om.tittel")}</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              {t("forside.om.tekst")}
            </p>
          </Reveal>
          <Reveal delay={140} className="flex justify-center">
            <div className="relative w-full max-w-[280px] sm:max-w-[320px]">
              <div className="absolute -inset-3 sm:-inset-4 rounded-[2.5rem] bg-primary/30 blur-2xl" aria-hidden />
              <LazyVideo
                src={aboutVideo}
                poster={aboutVideoPoster}
                className="relative w-full aspect-[2/3] object-cover rounded-[2rem] ring-2 ring-primary/60 shadow-luxe"
              />
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* FINAL CTA */}
      {t.vis("forside.cta.vis") && (
      <section data-ascendly-seksjon="forside.cta.vis" className="relative py-24 sm:py-32 md:py-44 overflow-hidden">
        <div className="absolute inset-0 gradient-radial" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-6xl md:text-7xl text-balance leading-[1]">
              {t("forside.cta.tittel")}
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-10">
              <Link
                to="/book"
                className="inline-flex items-center gap-2 rounded-full bg-primary-strong px-8 py-4 text-sm font-medium text-primary-foreground shadow-luxe lift"
              >
                {t("felles.bookknapp")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={280}>
            <div className="mt-8 text-sm text-muted-foreground">
              <a href={`mailto:${SITE.email}`} className="hover:text-primary">{SITE.email}</a>
              <span className="mx-2">·</span>
              <a href={SITE.phoneHref} className="hover:text-primary">{SITE.phone}</a>
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* FAQ — spørsmålene AEO-målingen viste at kundene faktisk stiller AI-assistenter */}
      {t.vis("forside.faq.vis") && (
      <section data-ascendly-seksjon="forside.faq.vis" className="py-20 sm:py-28 md:py-40">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
          <Reveal className="text-center">
            <div className="eyebrow">{t("forside.faq.stikktittel")}</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-4 text-balance">
              {t("forside.faq.tittel")}
            </h2>
          </Reveal>
          <Reveal delay={100} className="mt-10">
            <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-6">
              {AEO_FAQ_ITEMS.map((item) => (
                <AccordionItem key={item.q} value={item.q}>
                  <AccordionTrigger className="text-left font-serif text-lg">{item.q}</AccordionTrigger>
                  {/* forceMount: svaret ligger i HTML-en også når panelet er lukket (skjult med CSS
                      i accordion.tsx), saa AI-søk og Google leser det uten aa klikke. Utseendet er likt. */}
                  <AccordionContent forceMount className="text-muted-foreground leading-relaxed">
                    {item.a}
                    {"href" in item && item.href && (
                      <Link to={item.href as any} className="mt-3 block text-sm text-primary hover:underline">
                        Les hele svaret →
                      </Link>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={180} className="mt-8 flex justify-center">
            <Link to="/faq" className="text-sm text-foreground/85 hover:text-primary">
              {t("forside.faq.lenke")}
            </Link>
          </Reveal>
        </div>
      </section>
      )}
    </SiteLayout>
  );
}

// Unused import guard: keep type reference so SERVICE_PAGES stays in tree for tsgo
void SERVICE_PAGES;
